import { useState } from 'react';
import {
  HOLIDAY_SETS,
  dateParts,
  dateText,
  formatHolidays,
  holidaySet,
  holidayWhen,
  isPast,
  mergeHolidays,
  parseHolidays,
  type Holiday,
  type HolidaySet
} from '@stitch/pbx-adapter/holidays';
import { parseHours, clock } from '@stitch/pbx-adapter/describe';
import { Ranges } from './HoursEditor';
import { Icon } from '../lib/icons';
import { plural } from '../lib/format';

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** For a date input: yearly dates are shown in this year. */
function inputDate(v: string): string {
  const p = dateParts(v);
  if (!p) return '';
  return dateText({ ...p, year: p.year ?? new Date().getFullYear() });
}

function hoursText(hours: string, ruleOnDuringHours: boolean): string {
  if (!hours) return ruleOnDuringHours ? 'Off all day' : 'Closed all day';
  const iv = parseHours(hours);
  const text = iv ? iv.map((i) => `${clock(i.from)}–${clock(i.to)}`).join(', ') : hours;
  return ruleOnDuringHours ? `On ${text}` : `Open ${text}`;
}

/**
 * Holidays and special days for a time of day rule: closed all day, or special hours.
 * One-click US holidays (on the weekday they're observed), every-year dates, date ranges.
 */
export function HolidaysEditor({
  value,
  onChange,
  ruleOnDuringHours,
  error
}: {
  value: string;
  onChange: (v: string) => void;
  /** The rule is on during its hours (not an "open hours" rule): words change accordingly. */
  ruleOnDuringHours: boolean;
  error?: string;
}) {
  // Rows are kept locally so a half-made one (no date yet) isn't lost while typing.
  const [rows, setRows] = useState<Holiday[]>(() => parseHolidays(value) ?? []);
  const [editing, setEditing] = useState<number | null>(null);
  const [preset, setPreset] = useState('');
  const now = today();
  const year = Number(now.slice(0, 4));

  if (value.startsWith('raw:')) {
    return (
      <div className="holidays">
        <div className="recording">
          <Icon name="alert" size={15} /> These holidays were set up in the phone system in a form Stitch can’t show yet. They keep working as they are.
          <button type="button" className="link-btn" onClick={() => onChange('')}>
            Start the list over
          </button>
        </div>
      </div>
    );
  }

  const update = (next: Holiday[]) => {
    setRows(next);
    onChange(formatHolidays(next));
  };
  const set = (i: number, patch: Partial<Holiday>) => update(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const past = rows.filter((r) => r.from && isPast(r, now));
  const order = rows
    .map((r, i) => ({ r, i, key: r.from ? (isPast(r, now) ? `2${inputDate(r.from)}` : `1${inputDate(r.from)}`) : '0' }))
    .sort((a, b) => a.key.localeCompare(b.key));

  const presetOptions = HOLIDAY_SETS.flatMap((s) => [year, year + 1].map((y) => ({ value: `${s.id}:${y}`, label: `${s.label}, ${y}` })));

  return (
    <div className="holidays">
      <div className="preset-row" role="group" aria-label="Add holidays">
        <button
          type="button"
          className="preset"
          onClick={() => {
            const next = [...rows, { name: '', from: '', to: '', hours: '' }];
            setRows(next);
            setEditing(next.length - 1);
          }}
        >
          + Add a holiday
        </button>
        <label className="preset-select">
          <span className="sr-only">Add a set of holidays</span>
          <select
            className="input"
            value={preset}
            onChange={(e) => {
              const [set, y] = e.target.value.split(':');
              setPreset('');
              if (!set || !y) return;
              update(mergeHolidays(rows, holidaySet(set as HolidaySet, Number(y))));
            }}
          >
            <option value="">Add US holidays…</option>
            {presetOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {past.length > 0 && (
          <button type="button" className="preset" onClick={() => update(rows.filter((r) => !(r.from && isPast(r, now))))}>
            Remove {plural(past.length, 'past holiday')}
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="muted holidays-empty">No holidays yet. The rule follows the weekly hours every day of the year.</p>
      ) : (
        <ul className="holiday-list">
          {order.map(({ r, i }) => {
            const isOld = r.from !== '' && isPast(r, now);
            if (editing === i) {
              return (
                <li key={i} className="holiday-row editing">
                  <HolidayForm holiday={r} ruleOnDuringHours={ruleOnDuringHours} onChange={(patch) => set(i, patch)} onDone={() => setEditing(null)} />
                </li>
              );
            }
            return (
              <li key={i} className={`holiday-row${isOld ? ' past' : ''}`}>
                <span className="holiday-name">{r.name || 'Holiday'}</span>
                <span className="holiday-when">{r.from ? `${holidayWhen(r)}${dateParts(r.from)?.year ? `, ${dateParts(r.from)?.year}` : ''}` : 'No date yet'}</span>
                <span className="holiday-hours">{hoursText(r.hours, ruleOnDuringHours)}</span>
                <span className="row-actions">
                  <button type="button" className="icon-btn" aria-label={`Edit ${r.name || 'holiday'}`} title="Edit" onClick={() => setEditing(i)}>
                    <Icon name="edit" size={15} />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Remove ${r.name || 'holiday'}`}
                    title="Remove"
                    onClick={() => {
                      setEditing(null);
                      update(rows.filter((_, j) => j !== i));
                    }}
                  >
                    <Icon name="x" size={15} />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

function HolidayForm({
  holiday,
  ruleOnDuringHours,
  onChange,
  onDone
}: {
  holiday: Holiday;
  ruleOnDuringHours: boolean;
  onChange: (patch: Partial<Holiday>) => void;
  onDone: () => void;
}) {
  const yearly = holiday.from !== '' && dateParts(holiday.from)?.year === null;
  const [multi, setMulti] = useState(holiday.to !== '' && holiday.to !== holiday.from);
  const store = (iso: string, every: boolean) => (every && iso ? iso.slice(5) : iso);
  const setFrom = (iso: string) => {
    const from = store(iso, yearly);
    // Keep the last day on or after the first.
    const to = multi && holiday.to && inputDate(holiday.to) >= iso ? holiday.to : from;
    onChange({ from, to });
  };
  return (
    <div className="holiday-form">
      <div className="pair">
        <label className="field">
          <span className="field-label">Name</span>
          <input className="input" value={holiday.name} maxLength={40} placeholder="For example, Thanksgiving" onChange={(e) => onChange({ name: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">{multi ? 'From' : 'Date'}</span>
          <input className="input" type="date" value={inputDate(holiday.from)} onChange={(e) => setFrom(e.target.value)} />
        </label>
        {multi && (
          <label className="field">
            <span className="field-label">Through</span>
            <input
              className="input"
              type="date"
              value={inputDate(holiday.to)}
              min={inputDate(holiday.from)}
              onChange={(e) => onChange({ to: store(e.target.value, yearly) })}
            />
          </label>
        )}
      </div>
      <div className="holiday-checks">
        <label className="check">
          <input
            type="checkbox"
            checked={multi}
            disabled={!holiday.from}
            onChange={(e) => {
              setMulti(e.target.checked);
              if (!e.target.checked) onChange({ to: holiday.from });
            }}
          />
          More than one day
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={yearly}
            disabled={!holiday.from}
            onChange={(e) => {
              const from = inputDate(holiday.from);
              const to = inputDate(holiday.to || holiday.from);
              onChange({ from: store(from, e.target.checked), to: store(to, e.target.checked) });
            }}
          />
          Every year on this date
        </label>
      </div>
      <div className="radio-cards compact" role="radiogroup" aria-label={`${holiday.name || 'Holiday'} hours`}>
        <label className="radio-card">
          <input type="radio" checked={!holiday.hours} onChange={() => onChange({ hours: '' })} />
          <span>
            <strong>{ruleOnDuringHours ? 'Off all day' : 'Closed all day'}</strong>
          </span>
        </label>
        <label className="radio-card">
          <input type="radio" checked={Boolean(holiday.hours)} onChange={() => onChange({ hours: holiday.hours || '9:00A-1:00P' })} />
          <span>
            <strong>{ruleOnDuringHours ? 'On during special hours' : 'Open special hours'}</strong>
          </span>
        </label>
      </div>
      {holiday.hours && <Ranges label={holiday.name || 'Holiday'} value={holiday.hours} onChange={(hours) => onChange({ hours })} />}
      <div>
        <button type="button" className="btn secondary small" onClick={onDone} disabled={!holiday.from}>
          Done
        </button>
      </div>
    </div>
  );
}
